import type { ViewController } from '@presentation/shell/NexxusShell';
import type { Ecriture } from '@shared/types/Ecriture';
import type { Bulletin } from '@shared/types/Bulletin';
import { fmtFCFA, fmtDate, fmtPeriode, esc } from '@presentation/shell/format';
import { ToastService } from '@presentation/components/Toast';
import { confirmAction } from '@presentation/components/ConfirmDialog';
import { onExclusive } from '@presentation/shell/viewDom';
import { downloadCsv, downloadBlob } from '@presentation/shell/download';
import { PLAN_COMPTABLE_OHADA } from '@shared/constants/PlanComptable';
import { BalanceService } from '@domain/comptabilite/BalanceService';
import {
  lignesUtiles,
  totauxSaisieManuelle,
  validerSaisieManuelle,
  type LigneSaisieManuelle,
} from '@domain/comptabilite/SaisieManuelle';
import {
  genererFec,
  genererJournalCsv,
  genererSyntheseCsv,
} from '@domain/comptabilite/FecExporter';
import { uuid } from '@shared/utils/uuid';
import { SecureTenantStore } from '@infra/persistence/SecureTenantStore';
import {
  bulletinsSansEcriturePaie,
  generateEcrituresPaie,
} from '@domain/comptabilite/EcrituresPaieGenerator';
import {
  isModuleLicensed,
  PAIE_COMPTA_MODULE,
  PAIE_COMPTA_NOT_LICENSED_MESSAGE,
} from '@application/licence/ModuleGate';
import { recordAuditEvent } from '@application/audit/AuditDirectoryService';
import { canMutateComptaRecords } from '@application/access/ViewAccessPolicy';
import {
  isPeriodeCloturee,
  loadPeriodesCloturees,
  savePeriodesCloturees,
} from '@application/comptabilite/PeriodesCloturees';
import { deverrouillerPeriode } from '@application/comptabilite/CloturePeriode';
import { depsClotureDepuisContexte } from '@application/comptabilite/CloturePeriodeDeps';
import { loadComptesPaie } from '@application/comptabilite/ComptesPaieStore';
import {
  loadCompteurPieces,
  saveCompteurPieces,
} from '@application/comptabilite/CompteurPiecesStore';
import { calculerSceauPeriode } from '@domain/comptabilite/ScellementPeriode';
import { periodeCouranteFromTenant } from '@presentation/shell/periodeCourante';
import {
  allouerNumeroPiece,
  assertPieceNonSupprimablePhysiquement,
} from '@domain/comptabilite/NumerotationPieces';
import {
  assertDateDansExerciceOuvert,
  DateHorsExerciceError,
  motifDateHorsExercice,
  type FenetreExercice,
} from '@domain/comptabilite/ControlePeriodeExercice';
import { assertPieceEquilibree, PieceDesequilibreeError } from '@domain/comptabilite/EquilibrePiece';
import {
  assertSoldesEquilibresAvantCloture,
  BalancePeriodeDesequilibreeError,
} from '@domain/comptabilite/ControleSoldesCloture';
import {
  reconcilerPaiePeriode,
  resumeReconciliation,
} from '@domain/comptabilite/ReconciliationPaieCompta';
import { loadRegister } from '@presentation/shell/localCrud';
import {
  AVERTISSEMENT_DECEMBRE_MENSUEL,
  avertissementDecembreVerrouilleSeul,
} from '@application/comptabilite/PreparationClotureAnnuelle';
import { exerciceClotureDansRegistre, type ExerciceRegistre } from '@application/comptabilite/RegistreExercices';
import { activerOngletReglages, ongletReglagesDemande } from '@presentation/views/comptabilite/ongletReglages';
import { ouvrirDeverrouillage } from '@presentation/views/comptabilite/DeverrouillagePanel';
import { notifierToast } from '@presentation/views/comptabilite/panelKit';
import {
  monterReglagesComptables,
  ouvrirAssistantClotureAnnuelle,
} from '@presentation/views/comptabilite/reglagesComptaWiring';

interface CompteAgg {
  compte: string;
  libelle: string;
  debit: number;
  credit: number;
}

const PC_IDS = [
  '661100',
  '661200',
  '661300',
  '661400',
  '663100',
  '663200',
  '664100',
  '421000',
  '422000',
  '4313',
  '4314',
  '4421',
  '4471',
  '4472',
  '4473',
  '425000',
] as const;

function agregats(list: Ecriture[]): CompteAgg[] {
  const map = new Map<string, CompteAgg>();
  for (const e of list) {
    const cur = map.get(e.compte) ?? {
      compte: e.compte,
      libelle: e.libelle_compte || PLAN_COMPTABLE_OHADA[e.compte]?.libelle || '',
      debit: 0,
      credit: 0,
    };
    cur.debit += e.debit || 0;
    cur.credit += e.credit || 0;
    map.set(e.compte, cur);
  }
  return Array.from(map.values()).sort((a, b) => a.compte.localeCompare(b.compte));
}

function inPeriode(e: Ecriture, from: string, to: string): boolean {
  const p = e.periode || e.date.slice(0, 7);
  return (!from || p >= from) && (!to || p <= to);
}

export const comptabiliteController: ViewController = async (section, ctx, shell) => {
  let ecritures = await ctx.ecritures.list();
  const periodes = Array.from(new Set(ecritures.map((e) => e.periode))).sort((a, b) =>
    b.localeCompare(a),
  );
  const tenant = ctx.tenantContext.tryGetTenant();
  let current = periodeCouranteFromTenant(tenant, { periodes });
  const canMutate = canMutateComptaRecords(shell.permission);
  const isAdmin = shell.permission === 'admin';
  const tid = ctx.tenantContext.tryGetTenant()?.id ?? ctx.tenantContext.tenantId();
  let cloturees = await loadPeriodesCloturees(tid);
  const comptesPaie = await loadComptesPaie(tid);

  /** SYS-B-02 — exercices déclarés + repli sur l’exercice courant tenant. */
  const loadFenetresExercice = async (): Promise<FenetreExercice[]> => {
    const declared = await loadRegister<ExerciceRegistre>(tid, 'exercices');
    if (declared.length > 0) {
      return declared.map((e) => ({
        code: e.code || e.annee,
        date_debut: e.date_debut,
        date_fin: e.date_fin,
        statut: e.statut,
      }));
    }
    const an = tenant?.exercice_courant ?? String(new Date().getFullYear());
    return [
      {
        code: an,
        date_debut: `${an}-01-01`,
        date_fin: `${an}-12-31`,
        statut: 'ouvert',
      },
    ];
  };

  const label = section.querySelector('#cpt-periode-label');
  const picker = section.querySelector<HTMLInputElement>('#cpt-month-picker');
  const statutCloture = section.querySelector('#cpt-statut-cloture');
  const btnCloture = section.querySelector<HTMLButtonElement>('#btn-cpt-cloture');
  const btnDeverr = section.querySelector<HTMLButtonElement>('#btn-cpt-deverr');

  const periodeVerrouillee = (): boolean => isPeriodeCloturee(cloturees, current);

  const listPeriode = (): Ecriture[] => ecritures.filter((e) => e.periode === current);

  const paintCloture = (): void => {
    const closed = periodeVerrouillee();
    if (statutCloture) {
      statutCloture.textContent = closed ? 'Période clôturée' : 'Période ouverte';
    }
    if (btnCloture) btnCloture.hidden = closed;
    if (btnDeverr) {
      btnDeverr.style.display = closed ? '' : 'none';
      btnDeverr.hidden = !closed;
    }
    void paintAlerteDecembre();
  };

  /** Décembre : orienter vers la clôture annuelle ; avertir si décembre est verrouillé seul. */
  const paintAlerteDecembre = async (): Promise<void> => {
    const zone = section.querySelector<HTMLElement>('#cpt-alerte-decembre');
    if (!zone) return;
    const annee = current.slice(0, 4);
    let message: string | null = null;
    if (current.endsWith('-12') && !periodeVerrouillee()) {
      message = AVERTISSEMENT_DECEMBRE_MENSUEL;
    } else {
      const registre = await loadRegister<ExerciceRegistre>(tid, 'exercices');
      message = avertissementDecembreVerrouilleSeul({
        annee,
        cloturees,
        exerciceCloture: exerciceClotureDansRegistre(registre, annee),
      });
    }
    zone.textContent = message ?? '';
    zone.hidden = !message;
  };

  /** `periode` permet de verrouiller sur le mois de la date saisie, pas seulement sur le mois affiché. */
  const assertPeriodeOuverte = (periode: string = current): boolean => {
    if (!isPeriodeCloturee(cloturees, periode)) return true;
    ToastService.warning(
      `La période ${fmtPeriode(periode)} est clôturée. Déverrouillez-la pour modifier les écritures.`,
    );
    return false;
  };

  const render = (): void => {
    const list = listPeriode();
    if (label) label.textContent = fmtPeriode(current);
    if (picker) picker.value = current;
    renderSynthese(section, list);
    renderJournal(section, list);
    renderManuelles(section, list);
    paintCloture();
  };

  const refreshComptesGl = (): void => {
    const select = section.querySelector<HTMLSelectElement>('#gl-compte');
    if (!select) return;
    const comptes = [...new Set(ecritures.map((e) => e.compte))].sort();
    const prev = select.value;
    select.innerHTML =
      '<option value="">Sélectionnez un compte…</option>' +
      comptes
        .map((c) => {
          const lib = PLAN_COMPTABLE_OHADA[c]?.libelle ?? '';
          return `<option value="${esc(c)}">${esc(c)}${lib ? ' — ' + esc(lib) : ''}</option>`;
        })
        .join('');
    if (prev && comptes.includes(prev)) select.value = prev;
  };

  const w = window as unknown as Record<string, unknown>;
  w['changePeriode'] = (delta: number) => {
    const [y, m] = current.split('-').map(Number);
    const d = new Date(y ?? 2026, (m ?? 1) - 1 + delta, 1);
    current = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    render();
  };
  w['setPeriode'] = (value: string) => {
    if (value) {
      current = value;
      render();
    }
  };
  w['genererEcrituresPaie'] = () => {
    if (!isModuleLicensed(ctx, PAIE_COMPTA_MODULE)) {
      ToastService.warning(PAIE_COMPTA_NOT_LICENSED_MESSAGE);
      return;
    }
    if (!assertPeriodeOuverte()) return;
    void (async () => {
      const bulletins = await ctx.bulletins.list();
      const salaries = await ctx.salaries.list();
      const aGenerer = bulletinsSansEcriturePaie(bulletins, ecritures, current);
      /** Toutes les pièces sont construites et contrôlées (équilibre strict) avant la première écriture. */
      const aAjouter: ReturnType<typeof generateEcrituresPaie> = [];
      for (const b of aGenerer) {
        const salarie = salaries.find((s) => s.id === b.salarie_id);
        if (!salarie) continue;
        try {
          aAjouter.push(...generateEcrituresPaie(b, salarie, comptesPaie));
        } catch (err) {
          ToastService.error(
            `Écritures de paie refusées pour ${b.nom_complet ?? b.matricule} : ${err instanceof Error ? err.message : String(err)}`,
          );
          return;
        }
      }
      for (const e of aAjouter) await ctx.ecritures.add(e);
      const ajoutees = aAjouter.length;
      if (ajoutees === 0) {
        ToastService.info('Écritures de paie déjà à jour pour cette période');
        return;
      }
      await recordAuditEvent(ctx, {
        action: 'create',
        entity: 'ecriture',
        description: `${ajoutees} écriture(s) de paie — ${current}`,
      });
      ecritures = await ctx.ecritures.list();
      render();
      ToastService.success(`${ajoutees} écriture(s) générée(s) depuis les bulletins`);
    })();
  };
  w['cloturerPeriodeCompta'] = () => {
    if (!canMutate) {
      ToastService.warning('Clôture réservée aux profils comptable, RH et administrateur');
      return;
    }
    if (periodeVerrouillee()) {
      ToastService.info(`La période ${fmtPeriode(current)} est déjà clôturée`);
      return;
    }
    void (async () => {
      // SYS-B-05 — balance équilibrée obligatoire avant clôture.
      try {
        assertSoldesEquilibresAvantCloture(ecritures, current);
      } catch (err) {
        const msg =
          err instanceof BalancePeriodeDesequilibreeError
            ? err.message
            : `Clôture refusée : ${String(err)}`;
        ToastService.error(msg);
        return;
      }
      const decembre = current.endsWith('-12');
      const ok = await confirmAction({
        host: section,
        title: decembre ? 'Verrouiller décembre seul ?' : 'Clôturer la période',
        message: decembre
          ? `${AVERTISSEMENT_DECEMBRE_MENSUEL}\nSi vous verrouillez ${fmtPeriode(current)} ici, les écritures de fin d’année (amortissements, provisions, résultat) ne pourront plus être passées. Seul un administrateur pourra déverrouiller ensuite.`
          : `Les écritures de ${fmtPeriode(current)} seront verrouillées. Seul un administrateur pourra déverrouiller ensuite.`,
        confirmLabel: decembre ? 'Verrouiller décembre seul' : 'Clôturer',
        danger: decembre,
      });
      if (!ok) return;
      const deLaPeriode = ecritures.filter((e) => e.periode === current);
      const sceau = await calculerSceauPeriode(deLaPeriode);
      await SecureTenantStore.setJson(tid, `compta.sceau.${current}`, {
        periode: current,
        sceau,
        nb_ecritures: deLaPeriode.length,
        scelle_le: new Date().toISOString(),
      });
      cloturees = await savePeriodesCloturees(tid, [...cloturees, current]);
      await recordAuditEvent(ctx, {
        action: 'cloture',
        entity: 'ecriture',
        description: `Clôture de la période ${current} (sceau ${sceau.slice(0, 12)}…)`,
        payload: { sceau, nb_ecritures: deLaPeriode.length },
      });
      paintCloture();
      ToastService.success(`Période ${fmtPeriode(current)} clôturée et scellée`);
    })();
  };
  w['deverrouillerPeriodeCompta'] = () => {
    if (!isAdmin) {
      ToastService.warning('Seul l’administrateur peut déverrouiller une période clôturée');
      return;
    }
    if (!periodeVerrouillee()) {
      ToastService.info(`La période ${fmtPeriode(current)} n’est pas clôturée`);
      return;
    }
    void (async () => {
      const registre = await loadRegister<ExerciceRegistre>(tid, 'exercices');
      const periodeADeverrouiller = current;
      ouvrirDeverrouillage(section, {
        estAdmin: isAdmin,
        periode: periodeADeverrouiller,
        exerciceCloture:
          periodeADeverrouiller.endsWith('-12') &&
          exerciceClotureDansRegistre(registre, periodeADeverrouiller.slice(0, 4)),
        deverrouiller: (motif) =>
          deverrouillerPeriode(depsClotureDepuisContexte(ctx, tid), {
            periode: periodeADeverrouiller,
            estAdmin: isAdmin,
            motif,
          }),
        notify: notifierToast,
        apres: async () => {
          cloturees = await loadPeriodesCloturees(tid);
          paintCloture();
        },
      });
    })();
  };

  w['cloturerExerciceCompta'] = () => {
    ouvrirAssistantClotureAnnuelle(section, ctx, tid, canMutate, async () => {
      cloturees = await loadPeriodesCloturees(tid);
      ecritures = await ctx.ecritures.list();
      refreshComptesGl();
      render();
    });
  };

  const readLignesManuelles = (): LigneSaisieManuelle[] => {
    const rows = section.querySelectorAll<HTMLTableRowElement>('#cpt-man-tbody tr');
    return Array.from(rows).map((tr) => ({
      compte: tr.querySelector<HTMLInputElement>('[data-man="compte"]')?.value.trim() ?? '',
      libelle: tr.querySelector<HTMLInputElement>('[data-man="libelle"]')?.value.trim() ?? '',
      debit: Number(tr.querySelector<HTMLInputElement>('[data-man="debit"]')?.value || 0),
      credit: Number(tr.querySelector<HTMLInputElement>('[data-man="credit"]')?.value || 0),
      axe_cdc: tr.querySelector<HTMLInputElement>('[data-man="axe"]')?.value.trim() || undefined,
    }));
  };

  const refreshManTotaux = (): void => {
    const t = totauxSaisieManuelle(readLignesManuelles());
    const dEl = section.querySelector('#cpt-man-total-d');
    const cEl = section.querySelector('#cpt-man-total-c');
    const eq = section.querySelector<HTMLElement>('#cpt-man-equilibre');
    if (dEl) dEl.textContent = fmtFCFA(t.debit);
    if (cEl) cEl.textContent = fmtFCFA(t.credit);
    if (eq) {
      const ok = t.debit === t.credit && t.debit > 0;
      eq.textContent = ok ? 'Équilibrée' : t.debit === t.credit ? 'Montant nul' : 'Déséquilibrée';
      eq.style.color = ok ? 'var(--success)' : 'var(--danger)';
    }
  };

  const addLigne = (prefill?: Partial<LigneSaisieManuelle>): void => {
    const tbody = section.querySelector('#cpt-man-tbody');
    if (!tbody) return;
    const tr = document.createElement('tr');
    tr.innerHTML = `
      <td><input data-man="compte" class="mono" value="${esc(prefill?.compte ?? '')}" placeholder="421000" style="width:100%;padding:6px;"></td>
      <td><input data-man="libelle" value="${esc(prefill?.libelle ?? '')}" placeholder="Libellé ligne" style="width:100%;padding:6px;"></td>
      <td><input data-man="axe" value="${esc(prefill?.axe_cdc ?? '')}" placeholder="CDC" style="width:100%;padding:6px;" title="Centre de coût / axe analytique"></td>
      <td class="num"><input data-man="debit" type="number" min="0" value="${prefill?.debit ?? 0}" style="width:110px;padding:6px;text-align:right;"></td>
      <td class="num"><input data-man="credit" type="number" min="0" value="${prefill?.credit ?? 0}" style="width:110px;padding:6px;text-align:right;"></td>
      <td class="num"><button type="button" class="btn btn-ghost btn-sm" data-man-del>×</button></td>`;
    tbody.appendChild(tr);
    tr.addEventListener('input', refreshManTotaux);
    tr.querySelector('[data-man-del]')?.addEventListener('click', () => {
      tr.remove();
      refreshManTotaux();
    });
    refreshManTotaux();
  };

  w['addLigneManuelle'] = () => addLigne();
  w['resetEcritureManuelle'] = () => {
    const tbody = section.querySelector('#cpt-man-tbody');
    if (tbody) tbody.innerHTML = '';
    addLigne();
    addLigne();
    const date = section.querySelector<HTMLInputElement>('#cpt-man-date');
    const lib = section.querySelector<HTMLInputElement>('#cpt-man-libelle');
    if (date) date.value = new Date().toISOString().slice(0, 10);
    if (lib) lib.value = '';
    refreshManTotaux();
  };
  w['enregistrerEcritureManuelle'] = async () => {
    if (!canMutate) {
      ToastService.warning('Saisie réservée aux profils comptable, RH et administrateur');
      return;
    }
    if (!assertPeriodeOuverte()) return;
    const date = section.querySelector<HTMLInputElement>('#cpt-man-date')?.value ?? '';
    const libelle = section.querySelector<HTMLInputElement>('#cpt-man-libelle')?.value ?? '';
    const lignes = readLignesManuelles();
    const erreur = validerSaisieManuelle(lignes, date, libelle);
    if (erreur) {
      ToastService.error(erreur);
      return;
    }
    // SYS-B-02 — date dans l’exercice ouvert.
    const fenetres = await loadFenetresExercice();
    const horsExo = motifDateHorsExercice(date, fenetres);
    if (horsExo) {
      ToastService.error(horsExo);
      return;
    }
    try {
      assertDateDansExerciceOuvert(date, fenetres);
    } catch (err) {
      ToastService.error(err instanceof DateHorsExerciceError ? err.message : String(err));
      return;
    }
    const tenant_id = ctx.tenantContext.tenantId();
    const periode = date.slice(0, 7);
    if (!assertPeriodeOuverte(periode)) return;
    const utiles = lignesUtiles(lignes);
    // SYS-B-03 — équilibre bloquant avant persistance.
    try {
      assertPieceEquilibree(utiles);
    } catch (err) {
      ToastService.error(
        err instanceof PieceDesequilibreeError ? err.message : String(err),
      );
      return;
    }
    // SYS-B-01 — numérotation continue unique par exercice / société.
    const exerciceCode = (
      fenetres.find((f) => f.statut === 'ouvert')?.code ?? date.slice(0, 4)
    )
      .replace(/\D/g, '')
      .slice(0, 4);
    let compteur = await loadCompteurPieces(tenant_id, exerciceCode);
    const alloue = allouerNumeroPiece(compteur, 'OD', exerciceCode);
    compteur = alloue.compteur;
    const numeroPiece = alloue.numero;
    const rows = utiles.map((l) => ({
      id: uuid('ecr'),
      tenant_id,
      date,
      periode,
      journal: 'OD' as const,
      compte: l.compte.trim(),
      libelle_compte: PLAN_COMPTABLE_OHADA[l.compte.trim()]?.libelle ?? '',
      libelle: l.libelle || libelle,
      debit: l.debit,
      credit: l.credit,
      source: 'manuel' as const,
      axe_cdc: l.axe_cdc,
      numero_piece: numeroPiece,
      genere_le: new Date().toISOString(),
      genere_par: ctx.auth.getSession()?.user_id ?? 'manuel',
    }));
    try {
      const { runAtomicWrites } = await import('@application/persistence/AtomicBatch');
      await runAtomicWrites(
        rows.map((row) => ({
          table: 'ecritures',
          id: row.id,
          run: async () => {
            await ctx.ecritures.add(row);
          },
          rollback: async () => {
            await ctx.ecritures.delete(row.id);
          },
        })),
      );
      await saveCompteurPieces(tenant_id, exerciceCode, compteur);
    } catch (err) {
      ToastService.error(`Écriture non enregistrée (rollback) : ${String(err)}`);
      return;
    }
    ToastService.success(`Écriture manuelle enregistrée (${numeroPiece})`);
    ecritures = await ctx.ecritures.list();
    refreshComptesGl();
    (w['resetEcritureManuelle'] as () => void)();
    render();
  };

  /** SYS-B-03 C5 — état d’écart bulletins ↔ comptes de paie. */
  w['reconcilerPaieCompta'] = async () => {
    const [bulletins, salaries] = await Promise.all([ctx.bulletins.list(), ctx.salaries.list()]);
    const etat = reconcilerPaiePeriode(bulletins, ecritures, current, { salaries, comptes: comptesPaie });
    const msg = resumeReconciliation(etat);
    if (etat.ok) ToastService.success(msg);
    else ToastService.warning(msg);
  };

  /** SYS-B-01 — garde UI : pas de suppression physique d’une pièce séquentielle. */
  w['assertPieceNonSupprimable'] = (numero: string | undefined) => {
    try {
      assertPieceNonSupprimablePhysiquement(numero);
      return true;
    } catch (err) {
      ToastService.error(err instanceof Error ? err.message : String(err));
      return false;
    }
  };

  w['renderGrandLivre'] = () => {
    const compte = section.querySelector<HTMLSelectElement>('#gl-compte')?.value ?? '';
    const from = section.querySelector<HTMLInputElement>('#gl-from')?.value ?? '';
    const to = section.querySelector<HTMLInputElement>('#gl-to')?.value ?? '';
    const q = (section.querySelector<HTMLInputElement>('#gl-search')?.value ?? '').toLowerCase();
    if (!compte) {
      ToastService.warning('Choisissez un compte');
      return;
    }
    const avant = ecritures.filter((e) => e.compte === compte && from && (e.periode || '') < from);
    const soldeRep = avant.reduce((s, e) => s + (e.debit || 0) - (e.credit || 0), 0);
    const lignes = ecritures
      .filter((e) => e.compte === compte && inPeriode(e, from, to))
      .filter((e) => !q || e.libelle.toLowerCase().includes(q))
      .sort((a, b) => a.date.localeCompare(b.date));
    let solde = soldeRep;
    const tbody = section.querySelector('#gl-table tbody');
    if (tbody) {
      tbody.innerHTML =
        lignes.length === 0
          ? '<tr><td colspan="6" style="color:var(--gris);padding:16px;">Aucune ligne.</td></tr>'
          : lignes
              .map((e) => {
                solde += (e.debit || 0) - (e.credit || 0);
                return `<tr>
          <td>${esc(fmtDate(e.date))}</td>
          <td>${esc(e.journal)}</td>
          <td>${esc(e.libelle)}</td>
          <td class="num">${e.debit ? esc(fmtFCFA(e.debit)) : ''}</td>
          <td class="num">${e.credit ? esc(fmtFCFA(e.credit)) : ''}</td>
          <td class="num">${esc(fmtFCFA(solde))}</td>
        </tr>`;
              })
              .join('');
    }
    const count = section.querySelector('#gl-count');
    const rep = section.querySelector('#gl-solde-rep');
    const fin = section.querySelector('#gl-solde-fin');
    if (count) count.textContent = String(lignes.length);
    if (rep) rep.textContent = fmtFCFA(soldeRep);
    if (fin) fin.textContent = fmtFCFA(solde);
  };

  w['renderBalance'] = () => {
    const from = section.querySelector<HTMLInputElement>('#bal-from')?.value ?? current;
    const to = section.querySelector<HTMLInputElement>('#bal-to')?.value ?? current;
    const filtered = ecritures.filter((e) => inPeriode(e, from, to));
    const balance = BalanceService.calculer(filtered);
    const comptes = Object.values(balance).sort((a, b) => a.compte.localeCompare(b.compte));
    const tbody = section.querySelector('#bal-table tbody');
    const tfoot = section.querySelector('#bal-tfoot');
    const nb = section.querySelector('#bal-nb-comptes');
    if (nb) nb.textContent = String(comptes.length);
    let td = 0,
      tc = 0;
    if (tbody) {
      tbody.innerHTML =
        comptes.length === 0
          ? '<tr><td colspan="6" style="color:var(--gris);padding:16px;">Aucun mouvement.</td></tr>'
          : comptes
              .map((c) => {
                td += c.debit;
                tc += c.credit;
                const sd = c.solde > 0 ? c.solde : 0;
                const sc = c.solde < 0 ? -c.solde : 0;
                return `<tr>
          <td class="mono">${esc(c.compte)}</td>
          <td>${esc(c.libelle_compte)}</td>
          <td class="num">${esc(fmtFCFA(c.debit))}</td>
          <td class="num">${esc(fmtFCFA(c.credit))}</td>
          <td class="num">${sd ? esc(fmtFCFA(sd)) : ''}</td>
          <td class="num">${sc ? esc(fmtFCFA(sc)) : ''}</td>
        </tr>`;
              })
              .join('');
    }
    if (tfoot) {
      tfoot.innerHTML = `<tr class="total"><td colspan="2"><strong>Totaux</strong></td>
        <td class="num"><strong>${esc(fmtFCFA(td))}</strong></td>
        <td class="num"><strong>${esc(fmtFCFA(tc))}</strong></td>
        <td colspan="2"></td></tr>`;
    }
  };

  w['exportComptabiliteCSV'] = () => {
    downloadCsv(genererJournalCsv(listPeriode()), `journal-${current}.csv`);
    ToastService.success('Journal exporté');
  };
  w['exportSyntheseCSV'] = () => {
    downloadCsv(genererSyntheseCsv(agregats(listPeriode())), `synthese-${current}.csv`);
    ToastService.success('Synthèse exportée');
  };
  w['exportComptabiliteOHADA'] = () => {
    downloadCsv(genererJournalCsv(listPeriode()), `journal-ohada-${current}.csv`);
    ToastService.success('Journal exporté (colonnes OHADA)');
  };
  w['exportFEC'] = () => {
    const blob = new Blob([genererFec(listPeriode())], { type: 'text/plain;charset=utf-8;' });
    downloadBlob(blob, `FEC-${current.replace('-', '')}.txt`);
    ToastService.success('FEC généré');
  };

  const loadPlan = async (): Promise<void> => {
    const tid = ctx.tenantContext.tryGetTenant()?.id;
    const saved = tid
      ? await SecureTenantStore.getJson<Record<string, string>>(tid, 'compta.plan', {})
      : {};
    for (const code of PC_IDS) {
      const input = section.querySelector<HTMLInputElement>(`#pc-${code}`);
      if (input) input.value = saved[code] || code;
    }
  };
  w['savePlanComptable'] = async () => {
    if (!canMutate) {
      ToastService.warning('Action réservée aux profils comptable, RH et administrateur');
      return;
    }
    const tid = ctx.tenantContext.tryGetTenant()?.id;
    if (!tid) return;
    const plan: Record<string, string> = {};
    for (const code of PC_IDS) {
      const input = section.querySelector<HTMLInputElement>(`#pc-${code}`);
      const v = input?.value.trim() || code;
      if (!/^\d{2,8}$/.test(v)) {
        ToastService.error(`Compte invalide pour ${code}`);
        return;
      }
      plan[code] = v;
    }
    await SecureTenantStore.setJson(tid, 'compta.plan', plan);
    ToastService.success('Plan comptable enregistré');
  };
  w['resetPlanComptable'] = () => {
    for (const code of PC_IDS) {
      const input = section.querySelector<HTMLInputElement>(`#pc-${code}`);
      if (input) input.value = code;
    }
    ToastService.info('Valeurs SYSCOHADA restaurées — enregistrez pour les conserver');
  };

  const renderRappro = async (): Promise<void> => {
    const bulletins = (await ctx.bulletins.list()).filter((b) => b.periode === current);
    const tbody = section.querySelector('#rappro-table tbody');
    const tfoot = section.querySelector('#rappro-tfoot');
    const rapproDate = section.querySelector<HTMLInputElement>('#rappro-date-virement');
    const rapproRef = section.querySelector<HTMLInputElement>('#rappro-ref-banque');
    if (rapproDate && !rapproDate.value) rapproDate.value = new Date().toISOString().slice(0, 10);
    if (rapproRef && !rapproRef.value) rapproRef.value = `VIR-${current.replace('-', '')}`;
    if (!tbody) return;
    let total = 0;
    tbody.innerHTML =
      bulletins.length === 0
        ? '<tr><td colspan="6" style="color:var(--gris);padding:16px;">Aucun bulletin sur la période.</td></tr>'
        : bulletins
            .map((b) => {
              total += b.net_a_payer || 0;
              const paye = b.statut === 'paye';
              return `<tr>
        <td>${esc(b.nom_complet || b.matricule || b.salarie_id)}</td>
        <td class="num">${esc(fmtFCFA(b.net_a_payer))}</td>
        <td>${paye ? esc(fmtDate(b.date_virement || b.date_emission)) : '—'}</td>
        <td>${paye ? esc(b.reference_virement || '—') : '—'}</td>
        <td>${paye ? '<span class="pill success">Payé</span>' : '<span class="pill warning">Dû</span>'}</td>
        <td class="num">${paye ? '' : `<button type="button" class="btn btn-ghost btn-sm" data-rappro="${esc(b.id)}">Rapprocher</button>`}</td>
      </tr>`;
            })
            .join('');
    if (tfoot)
      tfoot.innerHTML = `<tr class="total"><td><strong>Total</strong></td><td class="num"><strong>${esc(fmtFCFA(total))}</strong></td><td colspan="4"></td></tr>`;
  };

  const marquerPayes = async (
    ids: string[],
    opts?: { date_virement?: string; reference_virement?: string },
  ): Promise<void> => {
    const dateVir = opts?.date_virement || new Date().toISOString().slice(0, 10);
    const refVir = opts?.reference_virement?.trim() || `VIR-${current.replace('-', '')}`;
    for (const id of ids) {
      const b = await ctx.bulletins.get(id);
      if (b && b.statut !== 'paye') {
        await ctx.bulletins.update({
          ...b,
          statut: 'paye',
          date_virement: dateVir,
          reference_virement: refVir,
        });
      }
    }
    await renderRappro();
  };

  const lireRapprochement = (): { date_virement: string; reference_virement: string } | null => {
    const date_virement =
      section.querySelector<HTMLInputElement>('#rappro-date-virement')?.value.trim() ?? '';
    const reference_virement =
      section.querySelector<HTMLInputElement>('#rappro-ref-banque')?.value.trim() ?? '';
    if (!date_virement) {
      ToastService.warning('Indiquez la date du virement');
      return null;
    }
    return {
      date_virement,
      reference_virement: reference_virement || `VIR-${current.replace('-', '')}`,
    };
  };

  onExclusive(section.querySelector('#rappro-table tbody'), 'click', (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLElement>('[data-rappro]');
    if (!btn?.dataset.rappro) return;
    if (!canMutate) {
      ToastService.warning('Action réservée aux profils comptable, RH et administrateur');
      return;
    }
    if (!assertPeriodeOuverte()) return;
    void (async () => {
      const opts = lireRapprochement();
      if (!opts) return;
      await marquerPayes([btn.dataset.rappro!], opts);
      ToastService.success('Rapprochement enregistré');
    })();
  });

  w['rapprocherTous'] = async () => {
    if (!canMutate) {
      ToastService.warning('Action réservée aux profils comptable, RH et administrateur');
      return;
    }
    if (!assertPeriodeOuverte()) return;
    const ok = await confirmAction({
      host: section,
      title: 'Rapprocher la période',
      message: `Tous les bulletins de ${fmtPeriode(current)} seront marqués comme payés.`,
      confirmLabel: 'Tout rapprocher',
    });
    if (!ok) return;
    const opts = lireRapprochement();
    if (!opts) return;
    const ids = (await ctx.bulletins.list())
      .filter((b: Bulletin) => b.periode === current && b.statut !== 'paye')
      .map((b) => b.id);
    await marquerPayes(ids, opts);
    ToastService.success('Rapprochement enregistré');
  };
  w['resetRapprochements'] = async () => {
    if (!canMutate) return;
    if (!assertPeriodeOuverte()) return;
    const ok = await confirmAction({
      host: section,
      title: 'Réinitialiser les rapprochements',
      message: `Les bulletins payés de ${fmtPeriode(current)} reviendront au statut validé.`,
      confirmLabel: 'Réinitialiser',
      danger: true,
    });
    if (!ok) return;
    const list = (await ctx.bulletins.list()).filter(
      (b) => b.periode === current && b.statut === 'paye',
    );
    for (const b of list) {
      await ctx.bulletins.update({
        ...b,
        statut: 'valide',
        date_virement: undefined,
        reference_virement: undefined,
      });
    }
    await renderRappro();
    ToastService.info('Rapprochements réinitialisés');
  };

  const glFrom = section.querySelector<HTMLInputElement>('#gl-from');
  const glTo = section.querySelector<HTMLInputElement>('#gl-to');
  const balFrom = section.querySelector<HTMLInputElement>('#bal-from');
  const balTo = section.querySelector<HTMLInputElement>('#bal-to');
  if (glFrom) glFrom.value = current;
  if (glTo) glTo.value = current;
  if (balFrom) balFrom.value = current;
  if (balTo) balTo.value = current;

  refreshComptesGl();
  (w['resetEcritureManuelle'] as () => void)();
  await loadPlan();
  render();
  await renderRappro();
  await monterReglagesComptables(section, ctx, tid, canMutate);
  if (ongletReglagesDemande()) activerOngletReglages(section);
};

function renderSynthese(section: HTMLElement, list: Ecriture[]): void {
  const comptes = agregats(list);
  const totalD = comptes.reduce((s, c) => s + c.debit, 0);
  const totalC = comptes.reduce((s, c) => s + c.credit, 0);

  const kpi = section.querySelector<HTMLElement>('#cpt-kpi-grid');
  if (kpi) {
    kpi.innerHTML = `
      <div class="kpi"><div class="label">Total débit</div><div class="value">${esc(fmtFCFA(totalD))}</div></div>
      <div class="kpi"><div class="label">Total crédit</div><div class="value">${esc(fmtFCFA(totalC))}</div></div>
      <div class="kpi"><div class="label">Comptes mouvementés</div><div class="value">${comptes.length}</div></div>
      <div class="kpi"><div class="label">Équilibre</div><div class="value" style="color:${totalD === totalC ? 'var(--success)' : 'var(--danger)'};">${totalD === totalC ? 'OK' : 'Déséquilibré'}</div></div>`;
  }

  const tbody = section.querySelector('#cpt-synthese-table tbody');
  if (tbody) {
    tbody.innerHTML =
      comptes.length === 0
        ? '<tr><td colspan="5" style="color:var(--gris);padding:16px;">Aucune écriture pour cette période</td></tr>'
        : comptes
            .map((c) => {
              const solde = c.debit - c.credit;
              return `<tr>
          <td class="mono">${esc(c.compte)}</td>
          <td>${esc(c.libelle)}</td>
          <td class="num">${esc(fmtFCFA(c.debit))}</td>
          <td class="num">${esc(fmtFCFA(c.credit))}</td>
          <td class="num" style="font-weight:600;">${esc(fmtFCFA(solde))}</td>
        </tr>`;
            })
            .join('');
  }
}

function renderJournal(section: HTMLElement, list: Ecriture[]): void {
  const nb = section.querySelector('#cpt-nb-ecritures');
  if (nb) nb.textContent = String(list.length);
  const tbody = section.querySelector('#cpt-journal-table tbody');
  if (!tbody) return;
  const sorted = [...list].sort(
    (a, b) => a.date.localeCompare(b.date) || a.compte.localeCompare(b.compte),
  );
  tbody.innerHTML =
    sorted.length === 0
      ? '<tr><td colspan="6" style="color:var(--gris);padding:16px;">Aucune écriture</td></tr>'
      : sorted
          .map(
            (e) => `<tr>
        <td>${esc(fmtDate(e.date))}</td>
        <td>${esc(e.salarie || e.matricule || '—')}</td>
        <td class="mono">${esc(e.compte)}</td>
        <td>${esc(e.libelle)}</td>
        <td class="num">${e.debit ? esc(fmtFCFA(e.debit)) : ''}</td>
        <td class="num">${e.credit ? esc(fmtFCFA(e.credit)) : ''}</td>
      </tr>`,
          )
          .join('');
}

function renderManuelles(section: HTMLElement, list: Ecriture[]): void {
  const manuelles = list.filter((e) => e.source === 'manuel');
  const count = section.querySelector('#cpt-man-count');
  if (count) count.textContent = String(manuelles.length);
  const groups = new Map<string, Ecriture[]>();
  for (const e of manuelles) {
    const key = e.numero_piece || e.id;
    const cur = groups.get(key) ?? [];
    cur.push(e);
    groups.set(key, cur);
  }
  const tbody = section.querySelector('#cpt-man-liste-table tbody');
  if (!tbody) return;
  const rows = [...groups.values()];
  tbody.innerHTML =
    rows.length === 0
      ? '<tr><td colspan="6" style="color:var(--gris);padding:16px;">Aucune écriture manuelle.</td></tr>'
      : rows
          .map((g) => {
            const first = g[0]!;
            const total = g.reduce((s, e) => s + (e.debit || 0), 0);
            return `<tr>
        <td>${esc(fmtDate(first.date))}</td>
        <td>${esc(first.libelle)}</td>
        <td class="num">${g.length}</td>
        <td class="num">${esc(fmtFCFA(total))}</td>
        <td>${esc(first.genere_par || '—')}</td>
        <td class="num">—</td>
      </tr>`;
          })
          .join('');
}
